import { useEffect, useMemo, useState } from 'react';
import { Alert, Spin, Space, Statistic, Row, Col, Card, Table } from 'antd';
import axios from 'axios';

import { parseTemplateWorkbook } from '../../utils/parseTemplateWorkbook';

const EMPTY_TEMPLATE_RESULT = {
    accounts: [],
    associatedAccounts: [],
    formulas: [],
    errors: [],
};


const TemplateImportValidation = ({ workbook, onResult }) => {
    const parseResult = useMemo(
    () => (workbook ? parseTemplateWorkbook(workbook) : EMPTY_TEMPLATE_RESULT),
    [workbook],
);

    const { accounts, associatedAccounts, formulas, errors: parseErrors } = parseResult;

    const shouldValidate = parseErrors.length === 0 &&
        (accounts.length || associatedAccounts.length || formulas.length);

    const [backendState, setBackendState] = useState({
        loading: false,
        result: null,
        error: null,
    });

    useEffect(() => {
        if (!shouldValidate) return;

        let cancelled = false;

        setBackendState({
            loading: true,
            result: null,
            error: null,
        });

        axios
            .post('/import/template/validate', {
                accounts,
                associatedAccounts,
                formulas,
            })
            .then((res) => {
                if (cancelled) return;
                setBackendState({
                    loading: false,
                    result: res.data,
                    error: null,
                });
            })
            .catch((err) => {
                if (cancelled) return;
                setBackendState({
                    loading: false,
                    result: null,
                    error: err?.response?.data?.error || err.message || 'Validation failed.',
                });
            });

        return () => {
            cancelled = true;
        };
    }, [shouldValidate, accounts, associatedAccounts, formulas]);

    
    const allErrors = useMemo(
    () => [...parseErrors, ...(backendState.result?.errors ?? [])],
    [parseErrors, backendState.result],
);

    useEffect(() => {
        onResult({
            accounts,
            associatedAccounts,
            formulas,
            errors: allErrors,
            warnings: [],
        });
    }, [onResult, accounts, associatedAccounts, formulas, allErrors]);

    const previewColumns = [
        { title: 'Sheet', dataIndex: 'sheet' },
        { title: 'Rows', dataIndex: 'rows' },
    ];

    const previewData = [
        { key: 'accounts', sheet: 'Accounts', rows: accounts.length },
        {
            key: 'associated',
            sheet: 'AssociatedAccounts',
            rows: associatedAccounts.length,
        },
        { key: 'formulas', sheet: 'Formulas', rows: formulas.length },
    ];

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {backendState.loading ? (
                <Alert
                    message={<Spin size="small" />}
                    description="Validating account structure against the database…"
                />
            ) : null}

            {backendState.error ? (
                <Alert
                    type="error"
                    showIcon
                    message="Validation request failed"
                    description={backendState.error}
                />
            ) : null}

            {!allErrors.length && backendState.result?.summary ? (
                <Card size="small">
                    <Row gutter={16}>
                        <Col span={8}>
                            <Statistic
                                title="Accounts"
                                value={backendState.result.summary.accounts}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Associated Accounts"
                                value={backendState.result.summary.associatedAccounts}
                            />
                        </Col>
                        <Col span={8}>
                            <Statistic
                                title="Formulas"
                                value={backendState.result.summary.formulas}
                            />
                        </Col>
                    </Row>
                </Card>
            ) : null}

            {allErrors.length ? (
                <Alert
                    type="error"
                    showIcon
                    message={`${allErrors.length} error${allErrors.length === 1 ? '' : 's'} — fix these to continue`}
                    description={
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {allErrors.slice(0, 30).map((err, i) => (
                                <li key={i}>
                                    <strong>{err.sheet}</strong> row {err.row},{' '}
                                    {err.field}: {err.code}
                                    {err.value != null ? ` (${err.value})` : ''}
                                </li>
                            ))}
                            {allErrors.length > 30 ? (
                                <li>…and {allErrors.length - 30} more</li>
                            ) : null}
                        </ul>
                    }
                />
            ) : null}

            <Table
                bordered
                size="small"
                pagination={false}
                columns={previewColumns}
                dataSource={previewData}
            />
        </Space>
    );
};

export default TemplateImportValidation;